/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Known action error failure states
 */
export const enum ActionErrorStatus {
    Timeout = 'Timeout',
    HookTimeout = 'Hook Timeout',
    ServiceUnavailable = 'Service Unavailable'
}

/**
 * Action Error Wrapper
 */
export class ActionError extends Error {
    public static readonly Name: string = 'ActionError';

    public error?: unknown;
    public status?: ActionErrorStatus;

    /**
     * Checks if an object is an action error
     */
    public static isActionError(error: unknown): boolean {
        return error instanceof Error && (<ActionError>error).name === ActionError.Name;
    }
    // ts-ignore
    public static isTimeoutError(error: unknown): boolean {
        return typeof error === 'object' && error !== null && (<ActionError>error).status === ActionErrorStatus.Timeout;
    }

    public static isServiceUnavailable(error: unknown): boolean {
        return typeof error === 'object' && error !== null && (<ActionError>error).status === ActionErrorStatus.ServiceUnavailable;
    }

    /**
     * Action Errors
     * @param error Original Error
     */
    constructor(error: ActionErrorStatus | Error);
    constructor(error: Error) {
        super();
        this.name = ActionError.Name;
        if (error instanceof Error) {
            this.message = error.message;
            this.error = error;
        } else {
            if (error === ActionErrorStatus.Timeout) {
                this.status = <ActionErrorStatus>error;
                this.message = 'Action Timeout';
            } else if (error === ActionErrorStatus.HookTimeout) {
                this.status = <ActionErrorStatus>error;
                this.message = 'Action hook Timeout';
            } else if (error === ActionErrorStatus.ServiceUnavailable) {
                this.status = <ActionErrorStatus>error;
                this.message = 'Action failed due to service unavailable';
            } else if (typeof error === 'string') {
                this.message = error;
            } else {
                this.error = error;
            }
        }

        // As of Typescript 2.1 if we extend the Error object the prototype must be set explicitly
        // See: https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, ActionError.prototype);
    }
}
